import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import ConversationView from "../components/assistant/ConversationView";
import { useAssistant } from "../components/assistant/AssistantProvider";

function SessionList({ sessions, activeId, onSelect, onNew, onDelete }) {
  return (
    <aside className="w-60 shrink-0 border-r border-sand-200 bg-surface hidden md:flex flex-col" data-testid="chat-session-list">
      <div className="p-3">
        <button onClick={onNew} className="btn-swiss btn-primary text-sm w-full" data-testid="chat-new-session-btn">
          <Plus size={15} className="mr-2" /> New chat
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
        {sessions.map((s) => (
          <div key={s.id}
            className={`group flex items-center gap-1 px-2.5 py-2 cursor-pointer text-sm border-l-2 transition-colors ${
              s.id === activeId ? "border-ember bg-sand-50 text-sand-950" : "border-transparent text-sand-600 hover:bg-sand-50"}`}
            onClick={() => onSelect(s.id)} data-testid={`chat-session-item-${s.id}`}>
            <span className="flex-1 truncate">{s.title || "New chat"}</span>
            <button onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
              className="opacity-0 group-hover:opacity-100 text-sand-400 hover:text-red-600 transition-opacity"
              data-testid={`chat-session-delete-${s.id}`}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </aside>
  );
}

export default function Chat() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const assistant = useAssistant();

  useEffect(() => {
    const expanded = assistant?.consumeExpandSession?.();
    api.get("/chat/sessions").then(({ data }) => {
      setSessions(data);
      if (expanded) setActiveId(expanded);
      else if (data.length > 0) setActiveId(data[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteSession = async (sid) => {
    try {
      await api.delete(`/chat/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.id !== sid));
      if (sid === activeId) setActiveId(null);
    } catch {
      toast.error("Could not delete chat");
    }
  };

  return (
    <div className="flex chat-shell-height" data-testid="chat-page">
      <SessionList sessions={sessions} activeId={activeId} onSelect={setActiveId} onNew={() => setActiveId(null)} onDelete={deleteSession} />
      <section className="flex-1 flex flex-col min-w-0 bg-sand-50">
        <ConversationView
          sessionId={activeId}
          onSessionCreated={(s) => { setActiveId(s.id); setSessions((x) => [s, ...x]); }}
          onAssistantDone={() => api.get("/chat/sessions").then(({ data }) => setSessions(data)).catch(() => {})}
          variant="full"
        />
      </section>
    </div>
  );
}
